import { apiClient } from './api'

export interface AdminPaymentDetail {
  id: string
  externalId: string
  enrollmentId?: string
  amount: number
  fee: number
  discount: number
  status: string
  paymentType: string
  refundStatus: string
  createdAt: string
  updatedAt: string
}

export interface AdminRefundResult {
  paymentId: string
  status: string
  refundStatus: string
  affectedEnrollments: number
}

interface ApiEnvelope<T> {
  success: boolean
  data: T
  error?: { code: string; message: string }
}

export const adminPaymentService = {
  /** Fetch a payment's admin-facing detail. */
  getDetail: async (paymentId: string): Promise<AdminPaymentDetail> => {
    const resp = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      `/admin/payments/${paymentId}`,
    )
    if (!resp.data?.success) {
      throw new Error(resp.data?.error?.message || 'Failed to fetch payment')
    }
    const raw = resp.data.data
    return {
      id: raw.id as string,
      externalId: raw.external_id as string,
      enrollmentId: raw.enrollment_id as string | undefined,
      amount: raw.amount as number,
      fee: raw.fee as number,
      discount: raw.discount as number,
      status: raw.status as string,
      paymentType: raw.payment_type as string,
      refundStatus: raw.refund_status as string,
      createdAt: raw.created_at as string,
      updatedAt: raw.updated_at as string,
    }
  },

  /** Issue an involuntary admin refund. */
  issueRefund: async (
    paymentId: string,
    body: { reason: string; notes: string },
  ): Promise<AdminRefundResult> => {
    const resp = await apiClient.post<ApiEnvelope<Record<string, unknown>>>(
      `/admin/payments/${paymentId}/refund`,
      { reason: body.reason, notes: body.notes },
    )
    if (!resp.data?.success) {
      throw new Error(resp.data?.error?.message || 'Refund failed')
    }
    const raw = resp.data.data
    return {
      paymentId: raw.payment_id as string,
      status: raw.status as string,
      refundStatus: raw.refund_status as string,
      affectedEnrollments: (raw.affected_enrollments as number) ?? 0,
    }
  },
}

export const REFUND_REASONS = [
  { value: 'event_cancelled', label: 'Event Cancelled' },
  { value: 'course_unpublished', label: 'Course Unpublished' },
  { value: 'double_charge', label: 'Double Charge' },
  { value: 'fraud', label: 'Fraud' },
  { value: 'other', label: 'Other' },
] as const

export default adminPaymentService
